import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CalendarCheck } from 'lucide-react';

import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { formatDateIntoPartials, isSameDate, isSameDay } from '../../../../util/dates';
import { isListingPathFor, readPendingBooking } from '../../../../util/pendingBooking';
import { isSignupEmailTakenError } from '../../../../util/errors';
import { buildSignupParams } from '../../../../util/signupParams';
import { login, signup, authenticationInProgress } from '../../../../ducks/auth.duck';
import { Modal } from '../../../../components';

import LoginForm from '../../../AuthenticationPage/LoginForm/LoginForm';
import SignupForm from '../../../AuthenticationPage/SignupForm/SignupForm';
import TermsAndConditions from '../../../AuthenticationPage/TermsAndConditions/TermsAndConditions';
import {
  SocialLoginButtonsMaybe,
  socialLoginProviderFlags,
} from '../../../AuthenticationPage/SocialLoginButtons/SocialLoginButtons';

import css from './LoginModal.module.css';

// The hosted user type a booking visitor is, when the marketplace defines one under this key.
const CUSTOMER_USER_TYPE = 'customer';

/**
 * The booking a signed-out visitor was in the middle of, if there is one for the page the dialog
 * was opened on.
 *
 * Read after mount rather than during render: `authmodal=open` can arrive on a server-rendered URL
 * (a reload with the dialog open, or a shared link), and sessionStorage does not exist there.
 * Reading it in the render pass would make the first client render disagree with the SSR markup.
 *
 * Scoped to the current path on purpose. The record outlives a dismissed dialog, and only the
 * listing page itself can resume it — so without this check, signing in from the topbar on the
 * search page would be dressed up as "one step left to your booking" and then keep none of it.
 * isListingPathFor, not a substring test: the same uuid appears in `/l/:slug/:id/checkout` and the
 * draft and pending-approval variants, none of which mount the resume hook.
 *
 * @param {boolean} isOpen
 * @returns {Object|null} the pending booking, when it belongs to the page underneath the dialog
 */
const usePendingBookingSummary = isOpen => {
  const [pending, setPending] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const stored = isOpen ? readPendingBooking() : null;
    setPending(stored && isListingPathFor(pathname, stored.listingId) ? stored : null);
  }, [isOpen, pathname]);
  return pending;
};

/**
 * "Your selection is safe" — the one thing a visitor stopped at an account wall needs to know.
 *
 * Without it the dialog reads as a dead end: they picked dates and a price variant, pressed the
 * CTA, and got asked to sign in. Showing the actual dates back to them is the difference between
 * "I have to start over" and "I just have to sign in".
 *
 * Everything is rendered in the listing's time zone, the same one the order panel used. Formatting
 * in the browser's instead would show a Zurich visitor 08:00 for the 09:00 Istanbul lounge slot
 * they just picked — on the one screen whose entire job is to prove nothing was lost.
 *
 * @param {Object} props
 * @param {Object} props.pending - the stored booking
 * @param {Object} props.intl
 */
const PendingBookingSummary = ({ pending, intl }) => {
  const { listingTitle, bookingStart, bookingEnd, seats, timeZone, isDateOnly } = pending || {};
  const start = bookingStart instanceof Date ? bookingStart : null;
  const end = bookingEnd instanceof Date ? bookingEnd : null;

  // formatDateIntoPartials rather than a hand-rolled format: it already owns the "spell out the
  // year only when it is not the current one" rule, and it applies that rule in the *listing's*
  // zone. Deciding it from the browser's calendar year was wrong across the new-year boundary — a
  // 1 Jan booking in Zurich is still 31 Dec for a visitor in Los Angeles, so the year that
  // disambiguates it was dropped exactly when it mattered.
  const partials = date => formatDateIntoPartials(date, intl, { timeZone });
  const day = date => partials(date).date;
  const time = date => partials(date).time;

  const dateRange = !start
    ? null
    : !end || isSameDate(start, end)
    ? day(start)
    : isDateOnly
    ? // Day, week, month and night units have no meaningful time of day.
      `${day(start)} – ${day(end)}`
    : isSameDay(start, end, timeZone)
    ? `${day(start)}, ${time(start)}–${time(end)}`
    : // A time-based slot that crosses midnight — a 23:00–01:00 lounge booking, say. Falling back
      // to a bare date range here told the visitor they had booked two days.
      `${partials(start).dateAndTime} – ${partials(end).dateAndTime}`;

  // Nothing was actually selected — an inquiry or an Abo panel has no fields at all — so there is
  // no selection to reassure anyone about. The dialog falls back to its ordinary welcome rather
  // than heading a fieldless flow "one step left to your booking".
  if (!dateRange) {
    return null;
  }

  return (
    <div className={css.savedSelection}>
      <CalendarCheck className={css.savedSelectionIcon} size={18} aria-hidden="true" />
      <div className={css.savedSelectionBody}>
        <p className={css.savedSelectionLabel}>
          <FormattedMessage id="LoginModal.selectionSaved" />
        </p>
        {listingTitle ? <p className={css.savedSelectionTitle}>{listingTitle}</p> : null}
        {dateRange ? (
          <p className={css.savedSelectionDates}>
            {dateRange}
            {seats > 1 ? (
              <>
                {' · '}
                <FormattedMessage id="LoginModal.selectionSeats" values={{ seats }} />
              </>
            ) : null}
          </p>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Login in a modal, instead of a navigation to /login.
 *
 * Measured on airbnb.ch (2026-08-06): "Einloggen oder registrieren" does not navigate — it opens a
 * centred dialog and leaves the URL untouched. Keeping the visitor on the page matters most on the
 * landing page, where sending someone to a separate auth screen loses the browsing context they
 * just built up.
 *
 * The /login route stays exactly as it was: it is still the target for deep links, redirects after
 * email verification, and anyone who opens the menu item in a new tab. This is an addition, not a
 * replacement.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.onClose - Called when the dialog is dismissed
 * @param {Function} props.onManageDisableScrolling - Body scroll lock handler
 * @param {string?} props.focusElementId - Element focus returns to when the dialog closes
 * @param {string?} props.from - Path the visitor is returned to after a social login. Password
 *   login and registration need no equivalent: both resolve in place and never leave the page.
 */
export const LoginModalComponent = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmitLogin,
    onSubmitSignup,
    authInProgress,
    loginError,
    signupError,
    focusElementId,
    from,
  } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const pendingBooking = usePendingBookingSummary(isOpen);

  // Which of the two forms the dialog is showing. Registration used to be a link out to /signup —
  // a full page load at the exact moment a visitor had picked their dates and pressed "book".
  // The selection survived that (sessionStorage), but the context did not, and most people who
  // reach this dialog from the booking CTA do not have an account yet. Now both forms live here
  // and the switch is a state change.
  const [mode, setMode] = useState('login');
  const isSignup = mode === 'signup';

  // Reopening the dialog starts from the login form again; without the reset it would reopen on
  // whichever form was last shown, including after a completed registration.
  useEffect(() => {
    if (!isOpen) {
      setMode('login');
    }
  }, [isOpen]);
  // Only dress the dialog as a booking when a booking was actually put together. The inquiry and
  // Abo panels have no fields, so their intent carries no dates — heading that "one step left to
  // your booking" over an empty "your selection is saved" card promises something that was never
  // selected, and "Buchung" is the wrong word for starting a membership.
  const hasBookingSelection = !!pendingBooking?.bookingStart;

  // Someone who just pressed "book this desk" is a customer, and the signup form's first question
  // is which of the marketplace's user types they are. Answering it for them removes a step that
  // has no right answer to get wrong here — picking "host" by mistake creates the wrong kind of
  // account — and reveals the e-mail field immediately instead of behind a dropdown.
  //
  // Looked up, never assumed: the user types come from the hosted config, so a marketplace that
  // does not have one keyed `customer` simply falls back to the ordinary signup page rather than
  // linking somewhere that 404s.
  const { userTypes = [], userFields = [] } = config.user || {};
  const customerUserType = userTypes.find(t => t.userType === CUSTOMER_USER_TYPE)?.userType;
  // Preselect "customer" only for someone who got here from a booking CTA. In the plain topbar
  // case the visitor may well be a host, and SignupForm asks the question itself when the
  // marketplace has more than one type.
  const preselectedUserType = pendingBooking ? customerUserType : null;

  // Returns undefined on purpose — see the note on AuthenticationPage's handler: a returned
  // promise rejects into final-form and is reported as unhandled.
  const handleSubmitSignup = values => {
    onSubmitSignup(buildSignupParams(values, userFields));
  };

  const errorMessage = isSignup ? (
    signupError ? (
      <div className={css.error} role="alert">
        {isSignupEmailTakenError(signupError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    ) : null
  ) : loginError ? (
    <div className={css.error} role="alert">
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  ) : null;

  return (
    <Modal
      id="TopbarLoginModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      className={css.modalRoot}
      containerClassName={css.modalContainer}
      contentClassName={css.scrollArea}
      closeButtonMessage={intl.formatMessage({ id: 'LoginModal.close' })}
      /* Without this, closing the dialog drops focus on <body> and a keyboard user restarts at
         the top of the page. Modal restores focus to this id on unmount. */
      focusElementId={focusElementId}
    >
      {/* Modal keeps its children mounted when closed, so without this guard a typed e-mail,
          a typed password and a previous "Anmeldung fehlgeschlagen" all survived closing and
          were still there on reopen. Remounting on each open clears the form. */}
      {isOpen ? (
        <>
          {/* Header row carrying the title, with the close control absolutely placed at its left.
          Without it the template's fixed top-right close button prints over the heading. */}
          <div className={css.header}>
            {/* Not an <h1>: the 16px bar label is a signpost, the 26px welcome below is the real
            heading. Marking this one up as the h1 inverted the document hierarchy. */}
            <p className={css.headerTitle}>
              <FormattedMessage id="TopbarDesktop.authCombined" />
            </p>
          </div>

          <div className={css.content}>
            <h1 className={css.welcome}>
              {isSignup ? (
                <FormattedMessage id="LoginModal.signupWelcome" />
              ) : hasBookingSelection ? (
                <FormattedMessage id="LoginModal.bookingWelcome" />
              ) : (
                <FormattedMessage id="LoginModal.welcome" />
              )}
            </h1>

            {/* Only for the booking path: the visitor arrived here mid-task, so the dialog has to
                say what the account is for and what it costs them (nothing, and no payment yet)
                before asking for an e-mail address. */}
            {hasBookingSelection ? (
              <>
                <PendingBookingSummary pending={pendingBooking} intl={intl} />
                <p className={css.bookingIntro}>
                  <FormattedMessage id="LoginModal.bookingIntro" />
                </p>
              </>
            ) : null}

            {/* Creating an account is the likely action here, so it gets to be one — not a line of
                small print below the password field, the reset-password link, a divider and three
                social buttons, which is where the login-first layout otherwise buries it. Most
                people who reach this dialog arrived from the booking CTA, and most of those do not
                have an account yet. Shown only in that context; the ordinary sign-in dialog keeps
                its usual bottom link (which is hidden here, so the two never compete). */}
            {pendingBooking && !isSignup ? (
              <button type="button" className={css.signupPrimary} onClick={() => setMode('signup')}>
                <FormattedMessage id="LoginModal.signupPrimary" />
              </button>
            ) : null}

            {errorMessage}

            {isSignup ? (
              <SignupForm
                className={css.form}
                formId="LoginModalSignupForm"
                onSubmit={handleSubmitSignup}
                inProgress={authInProgress}
                preselectedUserType={preselectedUserType}
                userTypes={userTypes}
                userFields={userFields}
                /* No open-handlers: inside a dialog the two documents open in a new tab rather
                   than in a second modal layer on top of this one. */
                termsAndConditions={
                  <TermsAndConditions formId="LoginModalSignupForm" intl={intl} />
                }
              />
            ) : (
              <LoginForm
                className={css.form}
                onSubmit={onSubmitLogin}
                inProgress={authInProgress}
              />
            )}

            {/* Google / Apple / Facebook, gated by whichever of them this deployment configures —
                the same component and the same env flags the /login page uses, so the two surfaces
                can never drift apart on which providers they offer.

                These leave the page: OAuth is a full redirect to the provider and back, so the
                dialog cannot survive it. `from` is what makes that acceptable — the server returns
                the visitor to the exact page they opened the dialog on instead of dumping them on
                the landing page. Two cases still land elsewhere by design: a first-time social user
                is sent to /confirm to pick a user type and accept the terms, and a failed sign-in
                is sent to /login, which is the surface that reads the st-autherror cookie. */}
            <SocialLoginButtonsMaybe
              className={css.socialButtons}
              isLogin={!isSignup}
              from={from}
              userType={isSignup ? preselectedUserType : undefined}
              {...socialLoginProviderFlags()}
            />

            {/* The one remaining way between the two forms. Hidden in the booking case while the
                login form is showing, because the prominent button above already offers it and
                two competing invitations to register is exactly the noise this dialog avoids. */}
            <p
              className={classNames(css.switch, {
                [css.switchHidden]: !!pendingBooking && !isSignup,
              })}
            >
              <FormattedMessage
                id={isSignup ? 'LoginModal.haveAccount' : 'LoginModal.noAccount'}
                values={{
                  signupLink: (
                    <button
                      type="button"
                      className={css.switchLink}
                      onClick={() => setMode(isSignup ? 'login' : 'signup')}
                    >
                      <FormattedMessage
                        id={isSignup ? 'LoginModal.loginLink' : 'LoginModal.signupLink'}
                      />
                    </button>
                  ),
                }}
              />
            </p>
          </div>
        </>
      ) : null}
    </Modal>
  );
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError } = state.auth;
  return {
    isAuthenticated,
    loginError,
    signupError,
    // The slice has loginInProgress / signupInProgress / …, never a combined `authInProgress`:
    // reading that name handed both forms `inProgress={undefined}`, so neither submit button ever
    // disabled itself. Harmless while the dialog only logged in; registration runs
    // create → login → fetchCurrentUser, which is seconds on a slow connection with nothing on
    // screen to say so.
    authInProgress: authenticationInProgress(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onSubmitLogin: ({ email, password }) => dispatch(login(email, password)),
  onSubmitSignup: params => dispatch(signup(params)),
});

const LoginModal = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LoginModalComponent);

export default LoginModal;
